<template>
  <div
    :class="[ns.e('cell-inner'), ns.is('valid-error', !!validError)]"
    @click.stop
  >
    <template v-if="column.type === 'checkbox'">
      <z-checkbox
        :model-value="isChecked"
        :size="controlSize"
        @update:model-value="onCheck"
      />
    </template>

    <template v-else-if="isUploadColumn">
      <div :class="ns.e('upload-cell')" @click.stop>
        <div :class="ns.e('upload-cell-summary')">
          <div v-if="column.type === 'image'" :class="imageCellClass">
            <template v-if="imageUrls.length === 1">
              <z-image
                :src="imageUrls[0]"
                fit="cover"
                :style="imageStyle"
                :preview-src-list="imageUrls"
                preview-teleported
              />
            </template>
            <template v-else-if="imageUrls.length > 1">
              <z-image
                v-for="(url, index) in visibleImageUrls"
                :key="`${url}-${index}`"
                :src="url"
                fit="cover"
                :style="imageStyle"
                :preview-src-list="imageUrls"
                :initial-index="index"
                preview-teleported
              />
              <div
                v-if="hiddenImageCount > 0"
                :class="ns.e('cell-images-more')"
              >
                <z-image
                  :src="imageUrls[imagePreviewStartIndex]!"
                  fit="cover"
                  :style="imageStyle"
                  :preview-src-list="imageUrls"
                  :initial-index="imagePreviewStartIndex"
                  preview-teleported
                />
                <span :class="ns.e('cell-images-more-label')">
                  +{{ hiddenImageCount }}
                </span>
              </div>
            </template>
          </div>
          <grid-file-summary
            v-else-if="column.type === 'file' || column.type === 'files'"
            :files="fileCellMetaList"
            :download="fileDownloadEnabled"
            :batch-download-action="column.batchDownloadAction"
            :batch-download-name="column.title"
          />
        </div>
        <grid-image-editor
          v-if="column.type === 'image' && showUploadTrigger"
          :row="row"
          :column="column"
          :size="controlSize"
          @update:value="setFieldValue"
        />
        <grid-file-editor
          v-if="
            (column.type === 'file' || column.type === 'files') &&
            showUploadTrigger
          "
          :row="row"
          :column="column"
          :size="controlSize"
          @update:value="setFieldValue"
        />
      </div>
      <z-tooltip
        v-if="
          (column.type === 'file' || column.type === 'files') &&
          fileCellSummaryLabel
        "
        ref="tooltipRef"
        trigger="manual"
        placement="bottom"
        :fallback-placements="['top', 'right', 'left']"
        effect="dark"
        :teleported="true"
        :enterable="false"
        @hide="onTooltipHide"
      />
    </template>

    <template v-else-if="column.type === 'switch' && editable">
      <div :class="ns.e('edit-control')" @click.stop>
        <z-switch
          v-model="switchModel"
          :size="controlSize"
          active-text="开"
          inactive-text="关"
          :active-value="true"
          :inactive-value="false"
          @click.stop
        />
      </div>
    </template>

    <template v-else-if="editing && editable">
      <div :class="ns.e('edit-control')" @click.stop>
        <z-input
          v-if="column.type === 'input' || column.type === 'html'"
          v-model="textModel"
          :size="controlSize"
          @click.stop
        />

        <z-input-number
          v-else-if="column.type === 'number'"
          v-model="numberModel"
          :size="controlSize"
          controls-position="right"
          @click.stop
        />

        <z-date-picker
          v-else-if="column.type === 'date' || column.type === 'datetime'"
          v-model="dateModel"
          :type="column.type === 'datetime' ? 'datetime' : 'date'"
          :value-format="dateValueFormat"
          :size="controlSize"
          @click.stop
        />

        <grid-lov-select
          v-else-if="isLovColumn"
          :row="row"
          :column="column"
          :size="controlSize"
          @change="onLovChange"
        />

        <z-input-table
          v-else-if="isTableColumn"
          v-model="inputTableModel"
          :source="row"
          :map="inputTableMap"
          :options="inputTableOptions"
          :before-open="column.beforeOpen"
          :display-text="displayText"
          :placeholder="column.title"
          :size="controlSize"
          @click.stop
        />

        <z-input v-else v-model="textModel" :size="controlSize" />
      </div>
    </template>

    <template v-else>
      <template v-if="showLovTag">
        <z-tag
          ref="textRef"
          :class="ns.e('cell-lov-tag')"
          :type="lovTagType"
          :size="controlSize"
          disable-transitions
          @mouseenter="onTextMouseEnter"
          @mouseleave="onTextMouseLeave"
        >
          {{ lovDisplayLabel }}
        </z-tag>
        <z-tooltip
          ref="tooltipRef"
          trigger="manual"
          placement="bottom"
          :fallback-placements="['top', 'right', 'left']"
          effect="dark"
          :teleported="true"
          :enterable="false"
          @hide="onTooltipHide"
        />
      </template>

      <template v-else-if="column.type === 'switch'">
        <span
          ref="textRef"
          :class="ns.e('cell-text')"
          @mouseenter="onTextMouseEnter"
          @mouseleave="onTextMouseLeave"
        >
          {{ switchDisplayText }}
        </span>
        <z-tooltip
          ref="tooltipRef"
          trigger="manual"
          placement="bottom"
          :fallback-placements="['top', 'right', 'left']"
          effect="dark"
          :teleported="true"
          :enterable="false"
          @hide="onTooltipHide"
        />
      </template>

      <template v-else>
        <a
          v-if="column.toDetail && displayText"
          ref="textRef"
          :class="[ns.e('cell-text'), ns.e('detail-link')]"
          href="javascript:void(0)"
          @mouseenter="onTextMouseEnter"
          @mouseleave="onTextMouseLeave"
          @click="onDetailClick"
        >
          {{ displayText }}
        </a>
        <span
          v-else
          ref="textRef"
          :class="ns.e('cell-text')"
          @mouseenter="onTextMouseEnter"
          @mouseleave="onTextMouseLeave"
        >
          {{ displayText }}
        </span>
        <z-tooltip
          ref="tooltipRef"
          trigger="manual"
          placement="bottom"
          :fallback-placements="['top', 'right', 'left']"
          effect="dark"
          :teleported="true"
          :enterable="false"
          @hide="onTooltipHide"
        />
      </template>
    </template>

    <div v-if="validError" :class="ns.e('cell-valid-error-tip')">
      <div :class="ns.e('cell-valid-error-bubble')">
        <span :class="ns.e('cell-valid-error-msg')">{{
          validError.content
        }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'

import {
  getLovLabel,
  getLovTagType,
  useLovCacheVersion,
  useNamespace,
} from '@zoryn/hooks'

import ZInput from '@zoryn/components/input'

import ZInputNumber from '@zoryn/components/input-number'

import ZDatePicker from '@zoryn/components/date-picker'

import ZCheckbox, {
  type CheckboxValueType,
} from '@zoryn/components/checkbox'

import ZTooltip from '@zoryn/components/tooltip'

import ZTag from '@zoryn/components/tag'

import ZImage from '@zoryn/components/image'

import ZSwitch from '@zoryn/components/switch'

import ZInputTable from '@zoryn/components/input-table'

import GridImageEditor from './grid-image-editor.vue'
import GridFileEditor from './grid-file-editor.vue'
import GridFileSummary from './grid-file-summary.vue'

import GridLovSelect from './grid-lov-select.vue'

import { getValidErrorKey } from '../methods/edit'

import {
  resolveLovDisplayField,
  resolveLovInputTableMap,
  resolveTableInputTableMap,
  resolveTableInputTableOptions,
} from '../utils/input-table-picker'

import { useOverflowTooltip } from '../hooks/use-overflow-tooltip'

import {
  formatSwitchDisplayText,
  resolveSwitchBoolean,
} from '../utils/switch-cell'
import {
  formatDateDisplayText,
  isDateColumnType,
  resolveDateValueFormat,
} from '../utils/date-cell'

import { resolveImageCellSize } from '../column/grid-size-metrics'
import {
  GRID_IMAGE_CELL_MAX_VISIBLE,
  resolveImageCellUrls,
} from '../utils/image-cell'
import {
  resolveFileCellDisplayName,
  resolveFileCellMetaList,
  resolveFileCellSummaryLabel,
} from '../utils/file-cell'

import { getRowId } from '../utils/row-id'
import { setCellFieldValue } from '../utils/cell-value'

import type { DataGrid, GridColumnInfo } from '../types'

const ns = useNamespace('grid')

const props = defineProps<{
  row: Record<string, unknown>

  column: GridColumnInfo

  editing: boolean

  editable: boolean
}>()

const $grid = inject('$grid') as DataGrid

const lovCacheVersion = useLovCacheVersion()

const controlSize = computed(() => $grid.gridSize.value)
const textRef = ref<HTMLElement>()
const tooltipRef = ref<{
  open?: (target: HTMLElement, content: string) => void | Promise<unknown>
  hide?: () => void | Promise<unknown>
}>()

function getOverflowContent() {
  if (showLovTag.value) {
    return lovDisplayLabel.value
  }
  if (props.column.type === 'switch') {
    return switchDisplayText.value
  }
  if (props.column.type === 'file' || props.column.type === 'files') {
    return fileCellSummaryLabel.value
  }
  return displayText.value
}

const { onTextMouseEnter, onTextMouseLeave, onTooltipHide } =
  useOverflowTooltip(() => tooltipRef.value, getOverflowContent)

const isChecked = computed(
  () => $grid.isCheckedByCheckboxRow?.(props.row) ?? false
)

const validError = computed(() => {
  const key = getValidErrorKey(getRowId(props.row), props.column.id)

  return $grid.reactData.validErrorMaps[key]
})

const isLovColumn = computed(
  () => props.column.type === 'lov' && !!props.column.lovType
)

const isTableColumn = computed(() => props.column.type === 'table')

const isUploadColumn = computed(
  () =>
    props.column.type === 'file' ||
    props.column.type === 'files' ||
    props.column.type === 'image'
)

const fileDownloadEnabled = computed(() => props.column.download !== false)

const fileCellMetaList = computed(() => {
  const { row, column } = props
  if (!column.field || (column.type !== 'file' && column.type !== 'files')) {
    return []
  }
  return resolveFileCellMetaList(row, column.field, column.type)
})

const fileCellSummaryLabel = computed(() =>
  resolveFileCellSummaryLabel(fileCellMetaList.value)
)

const showUploadTrigger = computed(
  () => props.editing && props.editable && isUploadColumn.value
)

const inputTableMap = computed(() =>
  props.column.type === 'table'
    ? resolveTableInputTableMap(props.column)
    : resolveLovInputTableMap(props.column)
)

const inputTableOptions = computed(() =>
  resolveTableInputTableOptions(props.column)
)

function isEmptyValue(val: unknown) {
  return val === null || val === undefined || val === ''
}

function setFieldValue(value: unknown) {
  const field = props.column.field

  if (field) {
    setCellFieldValue(props.row, field, value)
    $grid.options.onCellValueChange?.({
      row: props.row,
      field,
      value,
    })

    if (!isEmptyValue(value)) {
      $grid.clearValidate?.(props.row, props.column)
    }
  }
}

const textModel = computed({
  get: () => {
    const val = props.row[props.column.field!]

    return val == null ? '' : String(val)
  },

  set: (val: string) => setFieldValue(val),
})

const numberModel = computed({
  get: (): number | undefined => {
    const val = props.row[props.column.field!]

    if (val == null || val === '') return undefined

    const num = Number(val)

    return Number.isNaN(num) ? undefined : num
  },

  set: (val: number | undefined) => setFieldValue(val),
})

const switchModel = computed({
  get: () => resolveSwitchBoolean(props.row[props.column.field!]),
  set: (val: boolean) => {
    setFieldValue(val)
    $grid.clearValidate?.(props.row, props.column)
  },
})

const switchDisplayText = computed(() =>
  formatSwitchDisplayText(props.row[props.column.field!])
)

const dateModel = computed({
  get: () =>
    props.row[props.column.field!] as string | number | Date | null | undefined,

  set: (val: string | number | Date | null | undefined) => setFieldValue(val),
})

const dateValueFormat = computed(() => {
  const type = props.column.type === 'datetime' ? 'datetime' : 'date'
  return resolveDateValueFormat(props.row[props.column.field!], type)
})

const inputTableModel = computed({
  get: (): string | number | null => {
    const val = props.row[props.column.field!]

    if (val == null || val === '') return null

    if (typeof val === 'string' || typeof val === 'number') return val

    return null
  },

  set: (val: string | number | null | undefined) => setFieldValue(val ?? null),
})

const imageUrls = computed(() =>
  resolveImageCellUrls(props.row[props.column.field!])
)

const imageCellClass = computed(() => [
  imageUrls.value.length > 1 ? ns.e('cell-images') : ns.e('cell-image'),
])

const imageStyle = computed(() => {
  const size = resolveImageCellSize(controlSize.value)
  return {
    width: `${size}px`,
    height: `${size}px`,
  }
})

const visibleImageUrls = computed(() =>
  imageUrls.value.slice(0, GRID_IMAGE_CELL_MAX_VISIBLE)
)

const hiddenImageCount = computed(() =>
  Math.max(0, imageUrls.value.length - GRID_IMAGE_CELL_MAX_VISIBLE)
)

const imagePreviewStartIndex = computed(() =>
  Math.min(GRID_IMAGE_CELL_MAX_VISIBLE, imageUrls.value.length - 1)
)

const lovCellValue = computed(() => props.row[props.column.field!])

const lovDisplayLabel = computed(() => {
  void lovCacheVersion.value
  const { row, column } = props
  if (column.type !== 'lov' || !column.lovType) {
    return ''
  }
  const val = lovCellValue.value
  if (column.formatter) {
    return column.formatter({ row, column, cellValue: val })
  }
  const displayField = resolveLovDisplayField(column)
  if (displayField && row[displayField] != null && row[displayField] !== '') {
    return String(row[displayField])
  }
  const label = getLovLabel(column.lovType, val)
  if (label) {
    return label
  }
  if (val == null || val === '') {
    return ''
  }
  return String(val)
})

const showLovTag = computed(
  () =>
    props.column.type === 'lov' &&
    !!props.column.lovType &&
    !!lovDisplayLabel.value
)

const lovTagType = computed(() => {
  void lovCacheVersion.value
  const { column } = props
  if (column.lovTagType) {
    return column.lovTagType
  }
  return getLovTagType(column.lovType, lovCellValue.value) || 'info'
})

const displayText = computed(() => {
  void lovCacheVersion.value
  const { row, column } = props

  const val = row[column.field!]

  if (column.formatter) {
    return column.formatter({ row, column, cellValue: val })
  }

  if (column.type === 'table') {
    const displayField = resolveLovDisplayField(column)

    if (displayField && row[displayField] != null && row[displayField] !== '') {
      return String(row[displayField])
    }
  }

  if (column.type === 'lov' && column.lovType) {
    return getLovLabel(column.lovType, val)
  }

  if (column.type === 'switch') {
    return formatSwitchDisplayText(val)
  }

  if (column.type === 'file' || column.type === 'files') {
    const meta = row[`${column.field}Meta`]
    return resolveFileCellDisplayName(
      val,
      meta as Parameters<typeof resolveFileCellDisplayName>[1]
    )
  }

  if (isDateColumnType(column.type)) {
    return formatDateDisplayText(val, column.type)
  }

  if (val == null) return ''

  return String(val)
})

function onLovChange(val: unknown) {
  if (!isEmptyValue(val)) {
    $grid.clearValidate?.(props.row, props.column)
  }
}

function onCheck(val: CheckboxValueType) {
  $grid.handleCheckedRow?.(props.row, val === true)
}

function onDetailClick() {
  $grid.handleToDetail?.(props.row, props.column)
}
</script>
